import get from 'lodash/get';
import map from 'lodash/map';
import mapValues from 'lodash/mapValues';
import { createSelector } from 'reselect';

import { LANGUAGES } from '@savgroup-front-common/constants/shared';
import { BaseAutocompleteOptionForHookForm } from '@savgroup-front-common/types';

import bgCountryList from './country-list/country-bg.json';
import csCountryList from './country-list/country-cs.json';
import daCountryList from './country-list/country-da.json';
import deCountryList from './country-list/country-de.json';
import elCountryList from './country-list/country-el.json';
import enCountryList from './country-list/country-en.json';
import esCountryList from './country-list/country-es.json';
import fiCountryList from './country-list/country-fi.json';
import frCountryList from './country-list/country-fr.json';
import huCountryList from './country-list/country-hu.json';
import itCountryList from './country-list/country-it.json';
import nlCountryList from './country-list/country-nl.json';
import noCountryList from './country-list/country-no.json';
import plCountryList from './country-list/country-pl.json';
import ptCountryList from './country-list/country-pt.json';
import roCountryList from './country-list/country-ro.json';
import svCountryList from './country-list/country-sv.json';
import trCountryList from './country-list/country-tr.json';
import bgShortCountryList from './country-list/shortCountry-bg.json';
import csShortCountryList from './country-list/shortCountry-cs.json';
import daShortCountryList from './country-list/shortCountry-da.json';
import deShortCountryList from './country-list/shortCountry-de.json';
import elShortCountryList from './country-list/shortCountry-el.json';
import enShortCountryList from './country-list/shortCountry-en.json';
import esShortCountryList from './country-list/shortCountry-es.json';
import fiShortCountryList from './country-list/shortCountry-fi.json';
import frShortCountryList from './country-list/shortCountry-fr.json';
import huShortCountryList from './country-list/shortCountry-hu.json';
import itShortCountryList from './country-list/shortCountry-it.json';
import nlShortCountryList from './country-list/shortCountry-nl.json';
import noShortCountryList from './country-list/shortCountry-no.json';
import plShortCountryList from './country-list/shortCountry-pl.json';
import ptShortCountryList from './country-list/shortCountry-pt.json';
import roShortCountryList from './country-list/shortCountry-ro.json';
import svShortCountryList from './country-list/shortCountry-sv.json';
import trShortCountryList from './country-list/shortCountry-tr.json';
import bgLanguageList from './language-list/language-bg.json';
import csLanguageList from './language-list/language-cs.json';
import daLanguageList from './language-list/language-da.json';
import deLanguageList from './language-list/language-de.json';
import elLanguageList from './language-list/language-el.json';
import enLanguageList from './language-list/language-en.json';
import esLanguageList from './language-list/language-es.json';
import fiLanguageList from './language-list/language-fi.json';
import frLanguageList from './language-list/language-fr.json';
import huLanguageList from './language-list/language-hu.json';
import itLanguageList from './language-list/language-it.json';
import nativeLanguages from './language-list/language-native.json';
import nlLanguageList from './language-list/language-nl.json';
import noLanguageList from './language-list/language-no.json';
import plLanguageList from './language-list/language-pl.json';
import ptLanguageList from './language-list/language-pt.json';
import roLanguageList from './language-list/language-ro.json';
import svLanguageList from './language-list/language-sv.json';
import trLanguageList from './language-list/language-tr.json';
import { I18nAppState } from './types';

export const selectLocale = (state: { i18n: I18nAppState }): string =>
  state.i18n;

function formatCountryData(
  data: Record<string, string>,
): BaseAutocompleteOptionForHookForm[] {
  return map(data, (countryName, countryCode) => ({
    label: countryName,
    value: countryCode,
  }));
}

export enum LIST_KEY {
  COUNTRY_AUTOCOMPLETE_OPTION_LIST = 'countryList',
  SHORT_COUNTRY_AUTOCOMPLETE_OPTION_LIST = 'shortCountryList',
  COUNTRY_MAP = 'countryMap',
  LANGUAGE_MAP = 'languageMap',
}

const countryMapByLanguage: Record<string, Record<string, string>> = {
  [LANGUAGES.FR]: frCountryList,
  [LANGUAGES.EN]: enCountryList,
  [LANGUAGES.DE]: deCountryList,
  [LANGUAGES.NL]: nlCountryList,
  [LANGUAGES.ES]: esCountryList,
  [LANGUAGES.PT]: ptCountryList,
  [LANGUAGES.IT]: itCountryList,
  [LANGUAGES.PL]: plCountryList,
  [LANGUAGES.SV]: svCountryList,
  [LANGUAGES.FI]: fiCountryList,
  [LANGUAGES.NO]: noCountryList,
  [LANGUAGES.DA]: daCountryList,
  [LANGUAGES.CS]: csCountryList,
  [LANGUAGES.HU]: huCountryList,
  [LANGUAGES.RO]: roCountryList,
  [LANGUAGES.EL]: elCountryList,
  [LANGUAGES.TR]: trCountryList,
  [LANGUAGES.BG]: bgCountryList,
};

const shortCountryMapByLanguage: Record<string, Record<string, string>> = {
  [LANGUAGES.FR]: frShortCountryList,
  [LANGUAGES.EN]: enShortCountryList,
  [LANGUAGES.DE]: deShortCountryList,
  [LANGUAGES.NL]: nlShortCountryList,
  [LANGUAGES.ES]: esShortCountryList,
  [LANGUAGES.PT]: ptShortCountryList,
  [LANGUAGES.IT]: itShortCountryList,
  [LANGUAGES.PL]: plShortCountryList,
  [LANGUAGES.SV]: svShortCountryList,
  [LANGUAGES.FI]: fiShortCountryList,
  [LANGUAGES.NO]: noShortCountryList,
  [LANGUAGES.DA]: daShortCountryList,
  [LANGUAGES.CS]: csShortCountryList,
  [LANGUAGES.HU]: huShortCountryList,
  [LANGUAGES.RO]: roShortCountryList,
  [LANGUAGES.EL]: elShortCountryList,
  [LANGUAGES.TR]: trShortCountryList,
  [LANGUAGES.BG]: bgShortCountryList,
};

const languageMapByLanguage: Record<string, Record<string, string>> = {
  [LANGUAGES.FR]: frLanguageList,
  [LANGUAGES.EN]: enLanguageList,
  [LANGUAGES.DE]: deLanguageList,
  [LANGUAGES.NL]: nlLanguageList,
  [LANGUAGES.ES]: esLanguageList,
  [LANGUAGES.PT]: ptLanguageList,
  [LANGUAGES.IT]: itLanguageList,
  [LANGUAGES.PL]: plLanguageList,
  [LANGUAGES.SV]: svLanguageList,
  [LANGUAGES.FI]: fiLanguageList,
  [LANGUAGES.NO]: noLanguageList,
  [LANGUAGES.DA]: daLanguageList,
  [LANGUAGES.CS]: csLanguageList,
  [LANGUAGES.HU]: huLanguageList,
  [LANGUAGES.RO]: roLanguageList,
  [LANGUAGES.EL]: elLanguageList,
  [LANGUAGES.TR]: trLanguageList,
  [LANGUAGES.BG]: bgLanguageList,
};

export const list = {
  [LIST_KEY.COUNTRY_AUTOCOMPLETE_OPTION_LIST]: mapValues(
    countryMapByLanguage,
    (countries) => formatCountryData(countries),
  ),
  [LIST_KEY.COUNTRY_MAP]: countryMapByLanguage,
  [LIST_KEY.LANGUAGE_MAP]: languageMapByLanguage,
  [LIST_KEY.SHORT_COUNTRY_AUTOCOMPLETE_OPTION_LIST]: mapValues(
    shortCountryMapByLanguage,
    (countries) => formatCountryData(countries),
  ),
};

export const countryList = createSelector(
  [selectLocale],
  (locale): BaseAutocompleteOptionForHookForm[] => {
    return list[LIST_KEY.COUNTRY_AUTOCOMPLETE_OPTION_LIST]?.[locale] || [];
  },
);

export const shortCountryList = createSelector(
  [selectLocale],
  (locale): BaseAutocompleteOptionForHookForm[] => {
    return (
      list[LIST_KEY.SHORT_COUNTRY_AUTOCOMPLETE_OPTION_LIST]?.[locale] || []
    );
  },
);

export const countryMap = createSelector([selectLocale], (locale) => {
  return list[LIST_KEY.COUNTRY_MAP]?.[locale] || {};
});

export const selectLanguageMap = createSelector([selectLocale], (locale) => {
  return list[LIST_KEY.LANGUAGE_MAP]?.[locale] || {};
});

export const getCountryTranslationSelector = createSelector(
  [countryMap],
  (countries) =>
    (countryCode: string): string => {
      if (typeof countryCode !== 'string') {
        return '';
      }

      return get(countries, countryCode.toUpperCase(), '');
    },
);

export const getLanguageTranslationSelector = createSelector(
  [selectLanguageMap],
  (languages) => (languageCode: string) => {
    if (typeof languageCode !== 'string') {
      return null;
    }

    return get(languages, languageCode.toLowerCase(), null);
  },
);

export type GetNativeLanguageSelectorArgs =
  | 'en'
  | 'de'
  | 'fr'
  | 'es'
  | 'cs'
  | 'da'
  | 'et'
  | 'fi'
  | 'hr'
  | 'hu'
  | 'it'
  | 'lt'
  | 'lv'
  | 'nl'
  | 'no'
  | 'pl'
  | 'pt'
  | 'ro'
  | 'sk'
  | 'sl'
  | 'sq'
  | 'sv'
  | 'el'
  | 'tr'
  | 'bg';

export const getNativeLanguageSelector =
  () =>
  (languageCode: GetNativeLanguageSelectorArgs): string | null => {
    if (typeof languageCode !== 'string' || !languageCode) {
      return null;
    }

    return (
      nativeLanguages?.[
        languageCode.toLowerCase() as GetNativeLanguageSelectorArgs
      ] || null
    );
  };
